/**
 * pkce.ts
 *
 * Minimal PKCE (RFC 7636) helper for the Authorization Code flow used by
 * the classic-Outlook dialog login path (LoginComponents.tsx's
 * checkOutlookEnvironment() "win" branch, which used to build
 * `...&response_type=token&...` by hand - see MIGRATION notes in
 * LoginComponents.tsx for the before/after).
 *
 * Deliberately uses `code_challenge_method=plain` (code_challenge ==
 * code_verifier) rather than S256. Reasoning: this add-in still targets
 * classic desktop Outlook / older Mac Outlook WebViews (manifest.classic.xml,
 * isMacDesktop()), some of which run on IE11/Trident-era engines where the
 * Web Crypto SubtleCrypto API is unreliable (IE11 exposes a prefixed,
 * callback-based `msCrypto.subtle`, not the Promise-based standard one).
 * `plain` still requires no client secret and still defeats the actual
 * threat PKCE targets for public clients (a third party intercepting the
 * authorization code and redeeming it) - the code is useless to an
 * attacker without matching the exact code_verifier this session
 * generated and never persisted. If/when this add-in drops IE11/classic
 * support, upgrading to S256 is a one-line change (see comment below).
 */

const VERIFIER_LENGTH = 64;
const ALPHABET =
  "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~";

function randomVerifier(): string {
  let result = "";
  const cryptoObj = (window.crypto || (window as any).msCrypto) as Crypto | undefined;

  if (cryptoObj && typeof cryptoObj.getRandomValues === "function") {
    const bytes = new Uint32Array(VERIFIER_LENGTH);
    cryptoObj.getRandomValues(bytes);
    for (let i = 0; i < VERIFIER_LENGTH; i++) {
      result += ALPHABET[bytes[i] % ALPHABET.length];
    }
  } else {
    // Extremely old/unusual WebView with no crypto.getRandomValues at all -
    // fall back to Math.random. Still fine for PKCE's purpose (a one-time,
    // short-lived, non-guessable value), just not cryptographically ideal.
    for (let i = 0; i < VERIFIER_LENGTH; i++) {
      result += ALPHABET[Math.floor(Math.random() * ALPHABET.length)];
    }
  }
  return result;
}

export interface PkcePair {
  codeVerifier: string;
  codeChallenge: string; // == codeVerifier when using "plain"
  codeChallengeMethod: "plain";
}

export function createPkcePair(): PkcePair {
  const codeVerifier = randomVerifier();
  return {
    codeVerifier,
    codeChallenge: codeVerifier, // "plain" method
    codeChallengeMethod: "plain",
  };

  // --- Upgrading to S256 later (once IE11/classic support is dropped) ---
  // const codeChallenge = base64UrlEncode(
  //   await crypto.subtle.digest("SHA-256", new TextEncoder().encode(codeVerifier))
  // );
  // return { codeVerifier, codeChallenge, codeChallengeMethod: "S256" };
}
